import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ChangeDetectorRef, ElementRef } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Partner } from '@app/models/partner/IPartner';
import { AcvisService } from '@app/services/acvis.service';
import { AuthGuardService } from '@app/services/auth-guard.service';
import { OfferpackService } from '@app/services/offerpack.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'clever-offerpack-detail-pers',
    templateUrl: './offerpack-detail-pers.component.html',
    styleUrls: ['./offerpack-detail-pers.component.scss'],
    standalone: false
})
export class OfferpackDetailPersComponent implements OnInit, OnDestroy {
    @Input() datas: any;
    @Input() id: string;
    @Input() type: string;
    @Input() mitEmail: string;
    @Input() mitName: string;
    @Input() mitTel: string;
    @Input() packid: string;
    @Input() offerid: string;

    @Output() goBack: EventEmitter<boolean> = new EventEmitter();
    profileForm: FormGroup;
    travelersForm: FormGroup;
    zahlungForm: FormGroup;
    step: number = 0;
    asTouched: boolean = false;
    desktopTemplate: string = 'xl';
    mobileMode: boolean = false;
    checkDat: boolean = false;
    checkDatCh: boolean = false;
    checkDatPf: boolean = false;
    checkDatNor: boolean = false;
    sendEmailLoading: boolean = false;
    validErrorDatenschutz: boolean = false;
    validErrorDatenschutzCh: boolean = false;
    validErrorDatenschutzPF: boolean = false;
    validErrorDatenschutzNor: boolean = false;
    sendungresult: boolean = false;
    forms: number[] = [];
    currentPartner: Partner;
    payType: string = 'nach telefonischer Rücksprache';
    sengungrormessage: string = 'Sendung Fehler';

    private unsubscribe$ = new Subject();

    constructor(
        private offerpackService: OfferpackService,
        protected fb: FormBuilder,
        protected guardService: AuthGuardService,
        protected acvisService: AcvisService,
        protected mediaObserver: BreakpointObserver,
        protected el: ElementRef,
        protected router: Router,
        protected cdr: ChangeDetectorRef
    ) {
        if (this.mediaObserver.isMatched(Breakpoints.XSmall)) {
            this.desktopTemplate = 'xs';
            this.mobileMode = true;
        }
        if (this.mediaObserver.isMatched(Breakpoints.Small)) {
            this.desktopTemplate = 'sm';
            this.mobileMode = true;
        }
        if (this.mediaObserver.isMatched(Breakpoints.Medium)) {
            this.desktopTemplate = 'md';
            this.mobileMode = true;
        }
        if (this.mediaObserver.isMatched(Breakpoints.Large)) {
            this.desktopTemplate = 'lg';
            this.mobileMode = false;
        }
        if (this.mediaObserver.isMatched(Breakpoints.XLarge)) {
            this.desktopTemplate = 'xl';
            this.mobileMode = false;
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    ngOnInit(): void {
        this.guardService.getPartner$.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(partner => {
            this.currentPartner = partner;
        });
        this.initForm();
    }

    updateStep(ev) {
        if (this.profileForm.invalid || this.travelersForm.invalid || (this.payType === 'per Lastschrift' && this.zahlungForm.invalid)) {
            this.profileForm.markAllAsTouched();
            this.travelersForm.markAllAsTouched();
            this.zahlungForm.markAllAsTouched();
            this.asTouched = !this.asTouched;
            setTimeout(() => {
                this.goToError();
            }, 100);
        } else {
            this.asTouched = !this.asTouched;
            this.step = ev;
            this.el.nativeElement.scrollIntoView();
        }
        this.cdr.detectChanges();
    }

    async goToError() {
        const invalidElements = this.el.nativeElement.querySelectorAll('.mat-form-field-invalid');
        if (invalidElements.length > 0) {
            invalidElements[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }
    }

    retour(ev) {
        ev.stopPropagation();
        this.goBack.emit(true);
    }

    async initForm() {
        this.zahlungForm = this.fb.group({
            kontoinhaber: new FormControl('', [Validators.required, Validators.minLength(1)]),
            bankName: new FormControl('', [Validators.required, Validators.minLength(1)]),
            iban: new FormControl('', [Validators.required, Validators.minLength(1)]),
            bic: new FormControl('', [Validators.required, Validators.minLength(1)])
        });
        this.profileForm = this.fb.group({
            anmelder_anrede: new FormControl('', [Validators.required, Validators.minLength(1)]),
            anmelder_vorname: new FormControl('', [Validators.required, Validators.minLength(2)]),
            anmelder_name: new FormControl('', [Validators.required, Validators.minLength(2)]),
            anmelder_strasse: new FormControl('', [Validators.required, Validators.minLength(3)]),
            anmelder_hausnummer: new FormControl('', [Validators.required, Validators.minLength(1)]),
            anmelder_plz: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(5)]),
            anmelder_wohnort: new FormControl('', [Validators.required, Validators.minLength(2)]),
            anmelder_land: new FormControl('de', [Validators.required, Validators.minLength(2)]),
            anmelder_land_name: new FormControl('Deutschland', [Validators.minLength(2)]),
            anmelder_telefon: new FormControl('', [Validators.required, Validators.minLength(5)]),
            anmelder_email: new FormControl('', [Validators.required, Validators.email]),
            anmelder_geburtstag: new FormControl('', [Validators.required]),
            anmelder_monat: new FormControl('', [Validators.required]),
            anmelder_jahr: new FormControl('', [Validators.required]),
            anmelder_text: new FormControl('', [Validators.maxLength(600)]),
        });
        this.travelersForm = this.fb.group({
            fromKont: new FormControl(false, []),
        });
        let pax = this.datas?.pax;
        switch (this.type) {
            case 'getyourguide':
                pax = this.datas?.CustomOffer?.PAX;
                break;
            case 'profewo':
                pax = Number(this.datas?.FeWoDetailsResponse?.Kinder) + Number(this.datas?.FeWoDetailsResponse?.Erwachsene);
                break;
            case 'chamaeleon':
                pax = this.datas?.CustomOffer?.PAX;
                break;

            default:
                break;
        }
        for (let i = 0; i < Number(pax); i++) {
            this.forms.push(i + 1);
            this.travelersForm.addControl((i + 1) + '_anrede', new FormControl('', [Validators.required, Validators.minLength(1)]));
            this.travelersForm.addControl((i + 1) + '_vorname', new FormControl('', [Validators.required, Validators.minLength(1)]));
            this.travelersForm.addControl((i + 1) + '_name', new FormControl('', [Validators.required, Validators.minLength(1)]));
            this.travelersForm.addControl((i + 1) + '_geburtstag', new FormControl('', [Validators.required, Validators.minLength(2)]));
            this.travelersForm.addControl((i + 1) + '_monat', new FormControl('', [Validators.required, Validators.minLength(2)]));
            this.travelersForm.addControl((i + 1) + '_jahr', new FormControl('', [Validators.required, Validators.minLength(4)]));
            this.travelersForm.addControl((i + 1) + '_nat', new FormControl('', [Validators.required, Validators.minLength(2)]));
            this.travelersForm.addControl((i + 1) + '_land_name', new FormControl('', [Validators.required, Validators.minLength(2)]));
        }
        this.cdr.detectChanges();
    }

    gotoAgb() {
        const url = this.router.createUrlTree(['agb'], {}).toString();
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoAgbCh() {
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = this.datas?.CustomOffer?.Accommodation?.CatalogAgbUrl;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoAgbPF() {
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = this.datas?.FeWoDetailsResponse?.AGB_URI;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoDat() {
        const url = this.router.createUrlTree(['datenschutz'], {}).toString();
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoAgbNor() {
        const url = this.router.createUrlTree(['agb'], {}).toString();
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    sendAnfrag() {
        this.validErrorDatenschutz = false;
        this.validErrorDatenschutzCh = false;
        this.validErrorDatenschutzPF = false;
        this.validErrorDatenschutzNor = false;
        if (this.type === 'chamaeleon') {
            if (!this.checkDatCh) {
                this.validErrorDatenschutzCh = true;
            }
        }
        if (this.type === 'profewo') {
            if (!this.checkDatPf) {
                this.validErrorDatenschutzPF = true;
            }
        }
        if (!this.checkDatNor) {
            this.validErrorDatenschutzNor = true;
        }
        if (!this.checkDat) {
            this.validErrorDatenschutz = true;
        }

        if (!this.validErrorDatenschutz &&
            !this.validErrorDatenschutzCh &&
            !this.validErrorDatenschutzPF &&
            !this.validErrorDatenschutzNor
        ) {
            this.sendEmail();
        }
    }

    datenschutzChange(ev) {
        this.validErrorDatenschutz = !ev?.checked;
    }

    datenschutzChangeCh(ev) {
        this.validErrorDatenschutzCh = !ev?.checked;
    }

    datenschutzChangeNor(ev) {
        this.validErrorDatenschutzNor = !ev?.checked;
    }

    datenschutzChangePF(ev) {
        this.validErrorDatenschutzPF = !ev?.checked;
    }

    sendEmail() {
        this.sendEmailLoading = true;
        let offer;
        switch (this.type) {
            case 'getyourguide':
                offer = {
                    startDate: this.datas.CustomOffer.Accommodation?.StartDate,
                    endDate: this.datas.CustomOffer.Accommodation?.EndDate,
                    name: this.datas.CustomOffer.Accommodation?.AccommodationName,
                    childrenCount: this.datas.CustomOffer.ChildrenCount,
                    adults: this.datas.CustomOffer.adults,
                    price: this.datas.CustomOffer.Accommodation?.Price,
                    currency: this.datas.CustomOffer.Accommodation?.Currency,
                    veranstalter: this.datas?.CustomOffer?.TourOperatorName
                };
                break;
            case 'profewo':
                offer = {
                    startDate: this.datas.FeWoDetailsResponse?.Anreise_Datum,
                    endDate: this.datas.FeWoDetailsResponse?.Abreise_Datum,
                    name: this.datas.FeWoDetailsResponse?.Titel,
                    childrenCount: this.datas.FeWoDetailsResponse.Kinder,
                    adults: this.datas.FeWoDetailsResponse.Erwachsene,
                    price: this.datas.FeWoDetailsResponse?.Preis,
                    currency: 'EUR',
                    veranstalter: this.datas?.FeWoDetailsResponse?.VA_Name
                };
                break;
            case 'chamaeleon':
                offer = {
                    startDate: this.datas.CustomOffer.Accommodation?.StartDate,
                    endDate: this.datas.CustomOffer.Accommodation?.EndDate,
                    name: this.datas.CustomOffer.Accommodation?.AccommodationName,
                    childrenCount: this.datas.CustomOffer.ChildrenCount,
                    adults: this.datas.CustomOffer.adults,
                    price: this.datas.CustomOffer.Accommodation?.Price,
                    currency: this.datas.CustomOffer.Accommodation?.Currency,
                    veranstalter: this.datas?.CustomOffer?.Accommodation?.CatalogTourOperatorName
                };
                break;

            default:
                offer = {
                    startDate: this.datas?.hinflugs?.[0]?.startDate,
                    endDate: this.datas?.ruckflugs?.length > 0 ? this.datas.ruckflugs[0].startDate : '',
                    abflug: this.datas?.hinflugs?.[0]?.departureAirport?.name,
                    anflug: this.datas?.hinflugs?.[this.datas.hinflugs.length - 1]?.arrivalAirport?.name,
                    childrenCount: this.datas.childrenCount,
                    infantcount: this.datas.infantcount,
                    adults: this.datas.adult,
                    price: this.datas.price,
                    currency: this.datas.currency,
                    veranstalter: this.datas.type
                };
                break;
        }
        const travelers = [];
        for (let i = 0; i < this.forms.length; i++) {
            travelers.push({
                anrede: this.travelersForm.controls[this.forms[i] + '_anrede'].value,
                vorname: this.travelersForm.controls[this.forms[i] + '_vorname'].value,
                name: this.travelersForm.controls[this.forms[i] + '_name'].value,
                geburtstag: this.travelersForm.controls[this.forms[i] + '_geburtstag'].value,
                monat: this.travelersForm.controls[this.forms[i] + '_monat'].value,
                jahr: this.travelersForm.controls[this.forms[i] + '_jahr'].value,
                nat: this.travelersForm.controls[this.forms[i] + '_nat'].value,
                land_name: this.travelersForm.controls[this.forms[i] + '_land_name'].value,
            });
        }
        const target = {
            partnerid: this.currentPartner.partnerid,
            packid: this.packid,
            offerid: this.offerid,
            offer: offer,
            anmelder: {
                anmelder_anrede: this.profileForm.controls['anmelder_anrede'].value,
                anmelder_vorname: this.profileForm.controls['anmelder_vorname'].value,
                anmelder_name: this.profileForm.controls['anmelder_name'].value,
                anmelder_strasse: this.profileForm.controls['anmelder_strasse'].value,
                anmelder_hausnummer: this.profileForm.controls['anmelder_hausnummer'].value,
                anmelder_plz: this.profileForm.controls['anmelder_plz'].value,
                anmelder_wohnort: this.profileForm.controls['anmelder_wohnort'].value,
                anmelder_land: this.profileForm.controls['anmelder_land'].value,
                anmelder_land_name: this.profileForm.controls['anmelder_land_name'].value,
                anmelder_telefon: this.profileForm.controls['anmelder_telefon'].value,
                anmelder_email: this.profileForm.controls['anmelder_email'].value,
                anmelder_geburtstag: this.profileForm.controls['anmelder_geburtstag'].value,
                anmelder_monat: this.profileForm.controls['anmelder_monat'].value,
                anmelder_jahr: this.profileForm.controls['anmelder_jahr'].value,
                anmelder_text: this.profileForm.controls['anmelder_text'].value
            },
            travelers: travelers,
            type: this.type,
            mitEmail: this.mitEmail,
            mitName: this.mitName,
            mitTel: this.mitTel,
            payement: {
                type: this.payType,
                kontoinhaber: this.zahlungForm.controls['kontoinhaber'].value,
                bankName: this.zahlungForm.controls['bankName'].value,
                iban: this.zahlungForm.controls['iban'].value,
                bic: this.zahlungForm.controls['bic'].value
            }
        };

        this.offerpackService.request(target, 'sendAnmeldung', null).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((res) => {
            if (res?.error) {
                this.sendungresult = false;
            } else {
                this.sendungresult = true;
            }
            this.step = 3;
            this.sendEmailLoading = false;
            this.cdr.detectChanges();
        });
    }
}
